import axios, { InternalAxiosRequestConfig, AxiosError, HttpStatusCode } from 'axios';

import TokenStorageService from './TokenStorageService';
import NotificationService from './NotificationService';
import AuthService from './AuthService';
import PDISessionStorageService from './PDISessionStorageService';

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60 * 1000,
});

/** Axios request interceptor. */
axiosInstance.interceptors.request.use(request => requestHandler(request));

/** Axios response interceptor. */
axiosInstance.interceptors.response.use(...Array(1), error => errorHandler(error));

const axiosPDIAuthInstance = axios.create({
  baseURL: `${process.env.REACT_APP_PDI_AUTH_API_URL}`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60 * 1000,
});

/** Axios Auth PDI request interceptor. */
axiosPDIAuthInstance.interceptors.request.use(request => requestHandler(request));

/** Axios Auth PDI response interceptor. */
axiosPDIAuthInstance.interceptors.response.use(...Array(1), error => errorHandler(error));

const axiosPDIInstance = axios.create({
  baseURL: `${process.env.REACT_APP_PDI_API_URL}`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60 * 1000,
});

/** Axios PDI request interceptor. */
axiosPDIInstance.interceptors.request.use(request => {
  request.headers['Authorization'] = `Bearer ${PDISessionStorageService.getSessionToken()}`;

  return request;
});

/** Axios PDI response interceptor. */
axiosPDIInstance.interceptors.response.use(...Array(1), error => errorHandler(error));

/**
 * API request interceptor.
 *
 * @param request Axios request.
 */
const requestHandler = (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  if (request.url && !request.url.includes('auth')) {
    if (request.headers['Anonymous'] !== true) {
      request.headers['Authorization'] = `Bearer ${TokenStorageService.get()}`;
    }
  }

  return request;
};

let refreshTokenRequest: Promise<string> | null = null;

/**
 * Extracts error message from Axios error response.
 *
 * @param response Axios error response object.
 * @returns Extracted error message string.
 */
const extractErrorMessage = (response?: AxiosError['response']): string => {
  const data = response?.data;

  if (typeof data === 'string') {
    return data;
  }

  if (typeof data === 'object' && data !== null && 'title' in data && typeof data.title === 'string') {
    return data.title;
  }

  return response?.statusText || '';
};

/**
 * API error interceptor.
 *
 * @param error Axios error.
 */
const errorHandler = (error: AxiosError): Promise<AxiosError> | Promise<unknown> => {
  const { config, response, code } = error;
  const status = response?.status;
  const isAuthRequest = config?.url?.includes('auth');

  if (status === HttpStatusCode.Unauthorized && !isAuthRequest) {
    if (!refreshTokenRequest) {
      refreshTokenRequest = AuthService.refreshToken();
    }

    // Retry original request after refresh token.
    return refreshTokenRequest
      .then(accessToken => {
        TokenStorageService.set(accessToken);
        refreshTokenRequest = null;
        config.headers.Authorization = `Bearer ${accessToken}`;
        return axios(config);
      })
      .catch(() => {
        AuthService.logout();
        return Promise.reject(error);
      });
  }

  if (status === HttpStatusCode.NotImplemented || status === HttpStatusCode.InternalServerError) {
    NotificationService.push('warning', 'Network error.', 'Please try to refresh the page.');
  }

  if (code === 'ECONNABORTED') {
    NotificationService.push('warning', 'Server is not responding.', 'Please try again later.');
  }

  if (status === HttpStatusCode.Unauthorized) {
    NotificationService.push('warning', 'Authentication error.', extractErrorMessage(response));
  }

  if (status === HttpStatusCode.NotFound) {
    NotificationService.push('warning', 'Not Found.', extractErrorMessage(response));
  }

  if (status === HttpStatusCode.BadRequest) {
    NotificationService.push('warning', '', extractErrorMessage(response));
  }

  return Promise.reject(error);
};

export { axiosInstance, axiosPDIAuthInstance, axiosPDIInstance };
